import React from 'react';
import { Box, createStyles, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { BaseButton } from '@components/BaseButton/BaseButton';
import { BLOCK_RELEASE_REQUEST_CONFIRM_MODAL } from '@components/Modal/modalConstants';
import { useModalContext } from '@components/Modal/ModalContextProvider';
import { BlockReleaseRequestConfirmModal } from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestConfirmModal/BlockReleaseRequestConfirmModal';
import { PartialReleaseSection } from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestPage/BlockDetailsSection/PartialReleaseSection/PartialReleaseSection';
import { FormProvider, useForm } from 'react-hook-form';
import {
	PartialBlockReleaseRequestForm,
	PartialReleaseRequestDefaultValues,
} from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestPage/BlockDetailsSection/PartialReleaseSection/PartialReleaseRequest.types';
import BoxFancyScroll from '@components/BoxFancyScroll/BoxFancyScroll';
import { formatInTimeZone } from 'date-fns-tz';
import DisplaySimpleTimeFrame from '@components/DisplayTimeFrame/DisplaySimpleTimeFrame/DisplaySimpleTimeFrame';
import { getDateFromApiDateTime } from '@calendar/utilities';
import { getFormattedName } from '@utilities/commonFunction';
import { useSurgeons } from '@utilities/hooks/useSurgeons/useSurgeons';
import { SelectedReleaseInfo } from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestContext/BlockReleaseRequestContext.types';
import { useBlockReleaseRequestContext } from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestContext/BlockReleaseRequestContext';
import { useCreateRecommendedReleaseRejectionEvent } from '@data/blockReleases/hooks/useCreateRecommendedReleaseRejectionEvent';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';
import { useHistory } from 'react-router-dom';
import { ROUTE_CONSTANTS } from '@utilities/RouteConstants';
import { useToast } from '@utilities/hooks/useToast/useToast';
import { ToastConstants } from '@utilities/toastConstants';
import { ToastType } from '@interfaces/ToastStatus/ToastStatusState';
import { format } from 'date-fns';

const useStyles = makeStyles((theme) =>
	createStyles({
		root: {
			gridArea: 'blockDetails',
			padding: theme.spacing(3),
			borderRadius: theme.spacing(0.5),
			boxShadow: theme.shadows[9],
			maxHeight: 'fit-content',
			overflowY: 'auto',
		},
		sectionTitle: {
			fontSize: '1.5rem',
			marginBottom: theme.spacing(3),
		},
		itemTitle: {
			color: 'rgba(0, 0, 0, 0.6)',
			fontWeight: 325,
		},
		itemText: {
			margin: 0,
			marginBottom: theme.spacing(3),
			fontWeight: 600,
		},
		nameText: {
			fontSize: '1.25rem',
		},
		itemRow: {
			display: 'flex',
		},
		rowItem: {
			flex: '1 1 0',
		},
		error: {
			color: theme.palette.error.main,
		},
		warning: {
			color: theme.palette.warning.main,
		},
		success: {
			color: theme.palette.success.main,
		},
		keepBlockBtn: {
			padding: theme.spacing(0.875, 1),
		},
		releaseTimeBtn: {
			'&.Mui-disabled': {
				backgroundColor: theme.palette.grey[500],
				color: theme.palette.common.white,
			},
		},
	}),
);

interface BlockDetailsSectionProps {
	selectedReleaseInfo: SelectedReleaseInfo;
}

export const BlockDetailsSection = ({ selectedReleaseInfo }: BlockDetailsSectionProps) => {
	const classes = useStyles();
	const { openModal } = useModalContext();
	const history = useHistory();
	const displayToast = useToast();
	const { blockInUse } = useBlockReleaseRequestContext();
	const { createRecommendedReleaseRejectionEvent } = useCreateRecommendedReleaseRejectionEvent();

	const formMethods = useForm<PartialBlockReleaseRequestForm>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		criteriaMode: 'all',
		defaultValues: PartialReleaseRequestDefaultValues,
	});

	const { formState: { errors } } = formMethods;

	const { surgeonByNpi } = useSurgeons();

	if (!selectedReleaseInfo?.blockInfo || !selectedReleaseInfo?.facility || !selectedReleaseInfo.selectedRelease) {
		return null;
	}

	const { facility, blockInfo, selectedRelease } = selectedReleaseInfo;
	const { unit, blockStartTime, blockEndTime, startDateTime, npis, slotId, state, originalReleases } = selectedRelease;

	const surgeon = npis && surgeonByNpi[npis[0]];
	const surgeonName = surgeon && getFormattedName(surgeon);

	const timeZone = facility?.hospitalTimeZone ?? '';

	const formattedDate =
		startDateTime &&
		formatInTimeZone(
			getDateFromApiDateTime(startDateTime),
			timeZone,
			'eeee, MMMM d, yyyy',
		);

	const facilityName = facility?.name ?? '';
	const locationUnitString = unit ? ` / ${unit}` : '';
	const location = `${facilityName}${locationUnitString}`;

	const utilization = blockInfo?.utilization ?? 0;

	const handleKeepBlockClick = () => {
		if (state === BlockReleaseState.RECOMMENDED_RELEASE) {
			createRecommendedReleaseRejectionEvent(slotId, {
				event: BlockReleaseState.RECOMMENDED_RELEASE_REJECTED,
				body: {
					date: format(getDateFromApiDateTime(startDateTime), 'yyyy-MM-dd'),
				},
			});
		} else if ( state === BlockReleaseState.FLIP_RECOMMENDATION && originalReleases?.length) {
			originalReleases.forEach((release) => {
				if (release.state === BlockReleaseState.RECOMMENDED_RELEASE) {
					createRecommendedReleaseRejectionEvent(release.slotId, {
						event: BlockReleaseState.RECOMMENDED_RELEASE_REJECTED,
						body: {
							date: format(getDateFromApiDateTime(release.startDateTime), 'yyyy-MM-dd'),
						},
					});
				}
			});
		} else {
			displayToast(ToastType.SUCCESS, ToastConstants.BLOCK_KEPT);
			history.push(ROUTE_CONSTANTS.SCHEDULING, { disableClearToast: true });
		}
	};

	const handleReleaseTimeClick = () => {
		openModal(`${BLOCK_RELEASE_REQUEST_CONFIRM_MODAL.id}${slotId}`);
	};

	const hasValidationErrors = !!errors['startTime'] || !!errors['endTime'];

	return (
		<>
			<FormProvider {...formMethods}>
				<BlockReleaseRequestConfirmModal
					modalValues={BLOCK_RELEASE_REQUEST_CONFIRM_MODAL}
					blockReleaseInfo={blockInfo}
					facilityInfo={facility}
					selectedRelease={selectedRelease}
				/>
				<BoxFancyScroll className={classes.root}>
					<Typography component="h2" variant="h2" className={classes.sectionTitle}>
						Block Details
					</Typography>

					<Typography component="h3" className={classes.itemTitle}>
						Surgeon Name
					</Typography>
					<Typography className={`${classes.itemText} ${classes.nameText}`}>
						{surgeonName}
					</Typography>

					<Typography component="h3" className={classes.itemTitle}>
						Date
					</Typography>
					<Typography className={classes.itemText}>{formattedDate}</Typography>

					<Typography component="h3" className={classes.itemTitle}>
						Block time
					</Typography>
					{blockStartTime && blockEndTime && (
						<DisplaySimpleTimeFrame
							className={classes.itemText}
							startTime={blockStartTime}
							endTime={blockEndTime}
						/>
					)}
					<Typography component="h3" className={classes.itemTitle}>
						Location
					</Typography>
					<Typography className={classes.itemText}>{location}</Typography>

					<Box className={classes.itemRow}>
						<Box className={classes.rowItem}>
							<Typography component="h3" className={classes.itemTitle}>
								Available (approx.)
							</Typography>
							<Typography className={classes.itemText}>
								{blockInfo?.availableTimeDisplay}
							</Typography>
						</Box>

						<Box className={classes.rowItem}>
							<Typography component="h3" className={classes.itemTitle}>
								Utilization
							</Typography>
							<Typography
								className={classNames(classes.itemText, {
									[classes.success]: utilization >= 75,
									[classes.warning]: utilization >= 50 && utilization < 75,
									[classes.error]: utilization < 50,
								})}
							>
								{utilization}%
							</Typography>
						</Box>
					</Box>
					<PartialReleaseSection selectedReleaseInfo={selectedReleaseInfo} />
					<Box className={classes.itemRow} justifyContent="space-between">
						<BaseButton
							dataField="blockReleaseRequestPage-blockDetails-keepBlockBtn"
							variant="outlined"
							color="primary"
							className={classes.keepBlockBtn}
							onClick={handleKeepBlockClick}
							disabled={blockInUse}
						>
							Keep Block
						</BaseButton>
						<BaseButton
							className={classes.releaseTimeBtn}
							dataField="blockReleaseRequestPage-blockDetails-releaseTimeBtn"
							variant="contained"
							color="primary"
							onClick={handleReleaseTimeClick}
							disabled={hasValidationErrors || blockInUse}
						>
							Release Time
						</BaseButton>
					</Box>
				</BoxFancyScroll>
			</FormProvider>
		</>
	);
};
